import { useCallback, useEffect, useState } from 'react';
import { format, addDays } from 'date-fns';
import { api, downloadPdf, formatApiErrorDetail } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Snowflake, Printer, RefreshCw, CheckCircle2 } from 'lucide-react';

function num(n) {
  const v = Number(n) || 0;
  return v.toLocaleString('fi-FI', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export default function Kylmio() {
  const [current, setCurrent] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (date) => {
    if (!date) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/kylmio/${date}`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Kylmiö-listan lataus epäonnistui');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(current); }, [current, load]);

  const printLahete = async (customerId, customerName) => {
    try {
      await downloadPdf(`/api/lahete/single/${current}/${customerId}.pdf`, `Lahete_${customerName}_${current}.pdf`);
      toast.success(`Lähete tulostettu: ${customerName}`);
      await load(current);
    } catch (e) {
      toast.error(`Lähete ei onnistunut: ${e.message}`);
    }
  };

  const totalKg = rows.reduce((sum, r) => sum + (Number(r.maara_kg) || 0), 0);

  return (
    <div className="space-y-4" data-testid="kylmio-view">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-[#2A4B30] text-white flex items-center justify-center">
            <Snowflake size={20} />
          </div>
          <div>
            <h1 className="text-xl font-medium">Kylmiö</h1>
            <p className="text-xs text-[#6A6D66]">Asiakkaat ja määrät kylmiö-näkymässä</p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Input
            type="date"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            data-testid="kylmio-date"
            className="h-10 w-40"
          />
          <Button
            variant="outline"
            onClick={() => load(current)}
            data-testid="kylmio-reload"
            className="h-10 rounded-full"
          >
            <RefreshCw size={14} className="mr-1.5" /> Päivitä
          </Button>
        </div>
      </div>

      <div className="bg-[#E8F0E9] border border-[#CFE0D2] rounded-xl px-4 py-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
        <span><strong>{rows.length}</strong> asiakasta kylmiöllä</span>
        <span>yhteensä <span className="font-mono font-semibold">{num(totalKg)} kg</span></span>
      </div>

      {loading ? (
        <div className="text-sm text-[#6A6D66]">Ladataan…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-[#6A6D66] p-8 text-center border border-dashed border-[#E5E6E3] rounded-xl">
          Ei asiakkaita kylmiö-listalla tälle päivälle.<br />
          <span className="text-xs">Merkitse asiakas Asiakkaat-sivulla &quot;Näkyy kylmiöllä&quot; -täpällä.</span>
        </div>
      ) : (
        <div className="border border-[#E5E6E3] rounded-xl overflow-hidden">
          <table className="w-full" data-testid="kylmio-table">
            <thead className="bg-[#F4F5F0]">
              <tr>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-[#6A6D66]">Asiakas</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-[#6A6D66]">Määrä</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-[#6A6D66] w-24">Tulosta</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.customer_id}
                  className="border-t border-[#E5E6E3] hover:bg-[#FAFBF7]"
                  data-testid={`kylmio-row-${r.customer_id}`}
                >
                  <td className="px-4 py-4">
                    <div className="font-medium text-lg text-[#1C1C1A]">{r.customer_name}</div>
                    {r.ketju && <div className="text-xs text-[#6A6D66]">{r.ketju}</div>}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="font-mono font-semibold text-2xl text-[#2A4B30]">
                      {num(r.maara_kg)}
                    </span>
                    <span className="text-sm text-[#6A6D66] ml-1">kg</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <button
                      type="button"
                      onClick={() => printLahete(r.customer_id, r.customer_name)}
                      data-testid={`kylmio-print-${r.customer_id}`}
                      title="Tulosta lähete"
                      className="w-14 h-14 rounded-full bg-[#2A4B30] text-white hover:bg-[#1E3622] inline-flex items-center justify-center transition-colors relative"
                    >
                      <Printer size={22} />
                      {r.lahete === 'Tehty' && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white flex items-center justify-center">
                          <CheckCircle2 size={16} className="text-[#2A4B30]" />
                        </span>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
